
import { defineComponent } from 'vue';
import { IKeHoachSuaChuaResponseBase } from '@/core/interfaces/ApiResponses';
import { KeHoachSuaChuaService } from '@/core/services/KeHoachSuaChua.service';
import { swalErrNotification } from '@/core/helpers/utils';
import { generateTablePdf } from '@/core/helpers/generate-table-pdf';
import PdfViewerPage from '@/components/shared/pdf-viewer/PdfViewerPage.vue';
import { PDF_GENERATE_TABLE_DEFAULTS } from '@/components/shared/pdf-viewer/pdf-content.config';
import { pickDonViPdfExtra } from '@/components/shared/pdf-viewer/pdf-document-header';

const KE_HOACH_SUA_CHUA_PDF_COLUMNS = [
  { header: 'STT', width: 16, align: 'center' as const },
  { header: 'Số v/bản', width: 28, align: 'left' as const },
  { header: 'Cấp sửa chữa', width: 50, align: 'left' as const },
  { header: 'Nhóm toa xe', width: 36, align: 'left' as const },
  { header: 'Đơn vị', width: 40, align: 'left' as const },
  { header: 'Tháng', width: 18, align: 'center' as const },
  { header: 'Năm', width: 20, align: 'center' as const },
  { header: 'Ghi chú', width: 45, align: 'left' as const },
];

export default defineComponent({
  name: 'KeHoachSuaChuaListViewer',

  components: { PdfViewerPage },

  data() {
    return {
      items: [] as IKeHoachSuaChuaResponseBase[],
      searchTerm: '',
      tenDmToaXe: '',
      tenDonViFilter: '',
      pdfSource: null as string | null,
      isGenerating: false,
      tenDonVi: '',
      tenDonViCha: '',
    };
  },

  computed: {
    pdfFileName(): string {
      return 'danh-sach-ke-hoach-sua-chua.pdf';
    },

    subtitle(): string | undefined {
      const parts: string[] = [];
      if (this.tenDmToaXe) parts.push(`Nhóm toa xe: ${this.tenDmToaXe}`);
      if (this.tenDonViFilter) parts.push(`Đơn vị: ${this.tenDonViFilter}`);
      if (this.searchTerm) parts.push(`Từ khóa: ${this.searchTerm}`);
      return parts.length ? parts.join(' · ') : undefined;
    },
  },

  methods: {
    queryString(key: string): string {
      const value = this.$route.query[key];
      return typeof value === 'string' ? value : '';
    },

    queryNumber(key: string): number | undefined {
      const raw = this.queryString(key);
      if (!raw) return undefined;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    },

    async loadAndGenerate() {
      const idDmToaXe = this.queryNumber('idDmToaXe') ?? 0;
      const idDonVi = this.queryNumber('idDonVi');
      this.searchTerm = this.queryString('s');
      this.tenDmToaXe = this.queryString('tenDm');
      this.tenDonViFilter = this.queryString('tenDonVi');

      try {
        this.isGenerating = true;
        this.pdfSource = null;

        const listResult = await KeHoachSuaChuaService.listAll({
          s: this.searchTerm,
          id_dm_toa_xe: idDmToaXe,
          ...(idDonVi ? { id_don_vi: idDonVi } : {}),
        });
        this.items = listResult.items;
        const { tenDonVi, tenDonViCha } = pickDonViPdfExtra(listResult.extra);
        this.tenDonVi = tenDonVi;
        this.tenDonViCha = tenDonViCha;
        this.pdfSource = await this.generatePdf();
      } catch (e) {
        swalErrNotification(e, 'Có lỗi khi tải danh sách kế hoạch sửa chữa');
      } finally {
        this.isGenerating = false;
      }
    },

    async generatePdf(): Promise<string> {
      const rows = this.items.map((item, index) => [
        String(index + 1),
        item.so_van_ban ?? '',
        item.cap_sua_chua?.ten_cap_sc ?? '-',
        item.danh_muc_toa_xe?.ten_danh_muc ?? '-',
        item.don_vi?.ten_don_vi ?? '-',
        item.thang != null ? String(item.thang) : '-',
        item.nam != null ? String(item.nam) : '-',
        item.ghi_chu || '-',
      ]);

      return generateTablePdf({
        ...PDF_GENERATE_TABLE_DEFAULTS,
        title: 'DANH SÁCH KẾ HOẠCH SỬA CHỮA',
        subtitle: this.subtitle,
        columns: KE_HOACH_SUA_CHUA_PDF_COLUMNS,
        rows,
        documentHeader: {
          tenDonVi: this.tenDonVi,
          tenDonViCha: this.tenDonViCha,
          variant: 'masterList',
        },
      });
    },
  },

  mounted() {
    this.loadAndGenerate();
  },
});
